import React, { useState } from 'react';
import { useLazyGetSchemasQuery, useLazyGetSchemaLevelObjectsQuery } from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import {
  selectSnowflakeAccount,
  selectSnowflakeUsername,
  selectSnowflakeWarehouse,
  selectSnowflakeRole,
  selectSnowflakeAccountConnected,
  selectSnowflakePrivateKey,
  selectSnowflakePassphrase,
  setSnowflakeUsername,
  setSnowflakeWarehouse,
  setSnowflakeRole,
  setSnowflakeAccountConnected,
  setSnowflakePrivateKey,
  setSnowflakePassphrase,
  setSnowflakeAccountLabel,
  selectSnowflakeAccountLabel,
} from 'app/snowflakeSlice';
import { selectTablesFromStoredMetadata, selectIsPowerUserMode } from '../reducers/astraZenecaSlice';
import {
  selectSchemas,
  selectSchemaLevelObjects,
  selectSchemaLevelObject,
  addSchemas,
  addSchemaLevelObjects,
} from 'features/data-product-builder/reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import SnowflakeAccountForm from 'components/SnowflakeAccountForm';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';

export interface MetadataImportStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function SnowflakeConnectionStep(props: MetadataImportStepProps): JSX.Element {
  const dispatch = useAppDispatch();

  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const snowflakeAccountLabel = useAppSelector(selectSnowflakeAccountLabel);
  const snowflakeUsername = useAppSelector(selectSnowflakeUsername);
  const snowflakeWarehouse = useAppSelector(selectSnowflakeWarehouse);
  const snowflakeRole = useAppSelector(selectSnowflakeRole);
  const snowflakeAccountConnected = useAppSelector(selectSnowflakeAccountConnected);
  const snowflakePrivateKey = useAppSelector(selectSnowflakePrivateKey);
  const snowflakePassphrase = useAppSelector(selectSnowflakePassphrase);

  const tablesFromStoredMetadata = useAppSelector(selectTablesFromStoredMetadata);
  const isPowerUserMode = useAppSelector(selectIsPowerUserMode);

  const schemas = useAppSelector(selectSchemas);
  const schemaLevelObects = useAppSelector(selectSchemaLevelObjects);

  const [getSchemas] = useLazyGetSchemasQuery();
  const [getSchemaLevelObjects] = useLazyGetSchemaLevelObjectsQuery();

  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage] = useState<string>(
    'Failed to fetch the previously selected Snowflake objects from the selected account.',
  );

  const fetchSnowflakeData = async (): Promise<void> => {
    for (const table of tablesFromStoredMetadata) {
      const tableKey = `${String(table.database)}.${String(table.schema)}`;
      if (table.database !== undefined && !(table.database in schemas)) {
        const schemasRes: any = await getSchemas({
          account: snowflakeAccount,
          database: table.database,
        }).unwrap();

        const schemaArray = schemasRes.map((schema: any) => {
          return { database: table.database, schema };
        });
        dispatch(addSchemas({ databaseKey: table.database, schemas: schemaArray }));

        const tablesRes: any = await getSchemaLevelObjects({
          account: snowflakeAccount,
          database: table.database,
          schema: table.schema,
          object_types: ['table', 'view'],
        }).unwrap();

        if (tablesRes === undefined || tablesRes.table === undefined) {
          continue;
        }

        const tableArray = tablesRes.table.map((tableObj: any) => {
          return { database: table.database, schema: table.schema, table: tableObj };
        });
        dispatch(
          addSchemaLevelObjects({
            schemaKey: `${String(table.database)}.${String(table.schema)}`,
            schemaLevelObjects: tableArray,
          }),
        );
        dispatch(selectSchemaLevelObject(table));
      } else if (table.database !== undefined && table.schema !== undefined && !(tableKey in schemaLevelObects)) {
        const tablesRes: any = await getSchemaLevelObjects({
          account: snowflakeAccount,
          database: table.database,
          schema: table.schema,
          object_types: ['table', 'view'],
        }).unwrap();

        if (tablesRes === undefined || tablesRes.table === undefined) {
          continue;
        }

        const tableArray = tablesRes.table.map((tableObj: any) => {
          return { database: table.database, schema: table.schema, table: tableObj };
        });
        dispatch(
          addSchemaLevelObjects({
            schemaKey: `${String(table.database)}.${String(table.schema)}`,
            schemaLevelObjects: tableArray,
          }),
        );
        dispatch(selectSchemaLevelObject(table));
      } else {
        dispatch(selectSchemaLevelObject(table));
      }
    }
  };

  const onContinue = (): void => {
    if (
      !isPowerUserMode &&
      tablesFromStoredMetadata !== undefined &&
      Array.isArray(tablesFromStoredMetadata) &&
      tablesFromStoredMetadata.length > 0
    ) {
      setLoading(true);
      setFailed(false);
      fetchSnowflakeData()
        .then(() => {
          props.onContinue();
        })
        .catch((error) => {
          console.error('Error fetching Snowflake data:', error);
          setFailed(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      props.onContinue();
    }
  };

  return (
    <StepWrapper
      title="Connect to your Snowflake account"
      subtitle="Select to have the metadata from an existing Snowflake account through live interaction"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      continueDisabled={!snowflakeAccountConnected}
      isLoading={loading}
    >
      <div className="w-full flex flex-col items-center">
        <SnowflakeAccountForm
          snowflakeAccount={snowflakeAccountLabel}
          setSnowflakeAccount={(account) => dispatch(setSnowflakeAccountLabel(account))}
          snowflakeUsername={snowflakeUsername}
          setSnowflakeUsername={(username) => dispatch(setSnowflakeUsername(username))}
          snowflakeWarehouse={snowflakeWarehouse}
          setSnowflakeWarehouse={(warehouse) => dispatch(setSnowflakeWarehouse(warehouse))}
          snowflakeRole={snowflakeRole}
          setSnowflakeRole={(role) => dispatch(setSnowflakeRole(role))}
          snowflakeAccountConnected={snowflakeAccountConnected}
          setSnowflakeAccountConnected={(connected) => dispatch(setSnowflakeAccountConnected(connected))}
          snowflakePrivateKey={snowflakePrivateKey}
          setSnowflakePrivateKey={(key) => dispatch(setSnowflakePrivateKey(key))}
          snowflakePassphrase={snowflakePassphrase}
          setSnowflakePassphrase={(passphrase) => dispatch(setSnowflakePassphrase(passphrase))}
        />
        <LoadingAndErrorSection isLoading={loading} isFailed={failed} errorMessage={errorMessage} hideLoading={true} />
      </div>
    </StepWrapper>
  );
}
